import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "video-attachment__wrapper" }
const _hoisted_2 = { class: "video-attachment__container" }
const _hoisted_3 = {
  key: 0,
  class: "video-attachment__progress-container"
}
const _hoisted_4 = {
  key: 1,
  class: "video-attachment__icon-container video-attachment__icon-container--error"
}
const _hoisted_5 = {
  key: 2,
  class: "video-attachment__icon-container"
}
const _hoisted_6 = { key: 3 }
const _hoisted_7 = { key: 4 }
const _hoisted_8 = { key: 5 }
const _hoisted_9 = { key: 6 }
const _hoisted_10 = { key: 7 }
const _hoisted_11 = ["src", "loop"]
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 3,
  class: "video-attachment__badge video-attachment__badge--gif"
}
const _hoisted_14 = {
  key: 4,
  class: "video-attachment__duration"
}

import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import type { Attachment } from "@/dtos/Unipile.dto";
import { Icon } from "@iconify/vue";
import { useAttachmentLoader } from "@/composables/attachments/useAttachmentLoader";
import { useAttachmentDownload } from "@/composables/attachments/useAttachmentDownload";
import ProgressCircle from "./ProgressCircle.vue";

interface Props {
  attachment: Attachment;
  messageId?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoAttachment',
  props: {
    attachment: {},
    messageId: {}
  },
  setup(__props: any) {

const props = __props;

const rootEl = ref<HTMLElement | null>(null);
const videoLoaded = ref(false);
const loadError = ref(false);

const {
  isLoading,
  loadingProgress,
  loadedBytes,
  blobUrl,
  loadAttachment,
  getAttachmentUrl,
  error,
  cleanup,
} = useAttachmentLoader(props.attachment, props.messageId, () => {});

const {
  isDownloading,
  loadingProgress: downloadProgress,
  download,
} = useAttachmentDownload(props.attachment, props.messageId);

const resolvedVideoUrl = computed(() => {
  if (blobUrl.value) return blobUrl.value;
  return getAttachmentUrl(props.attachment.url);
});

const loadVideo = async () => {
  if (isLoading.value) return;
  loadError.value = false;
  const blob = await loadAttachment("video/mp4");
  if (blob) {
    videoLoaded.value = true;
  } else if (error.value) {
    loadError.value = true;
  }
  // null without error.value = aborted (unmount/retry) — do nothing
};

const handleDownload = () => {
  download("video/mp4", `video_${props.attachment.id}`);
};

watch(
  () => resolvedVideoUrl.value,
  (url) => {
    const attachmentUrl = props.attachment.url;
    if (url && attachmentUrl && !attachmentUrl.startsWith("att://")) {
      videoLoaded.value = true;
    }
  },
  { immediate: true }
);

let _observer: IntersectionObserver | null = null;

onMounted(() => {
  const url = props.attachment.url;
  if (!url || url.startsWith("att://")) {
    const el = rootEl.value;
    if (!el) {
      loadVideo();
      return;
    }
    _observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          _observer?.disconnect();
          _observer = null;
          loadVideo();
        }
      },
      { threshold: 0.1 }
    );
    _observer.observe(el);
  }
});

onUnmounted(() => {
  _observer?.disconnect();
  _observer = null;
  cleanup();
});

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "video-attachment",
    ref_key: "rootEl",
    ref: rootEl
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        (!videoLoaded.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "video-attachment__placeholder",
              onClick: loadVideo
            }, [
              (_unref(isLoading))
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (_unref(loadingProgress) > 0)
                      ? (_openBlock(), _createBlock(ProgressCircle, {
                          key: 0,
                          progress: _unref(loadingProgress),
                          size: "medium",
                          "bg-stroke": "rgba(255, 255, 255, 0.2)",
                          "fill-stroke": "white"
                        }, null, 8, ["progress"]))
                      : (_openBlock(), _createBlock(_unref(Icon), {
                          key: 1,
                          icon: "mdi:loading",
                          class: "video-attachment__preview-icon video-attachment__preview-icon--spin"
                        }))
                  ]))
                : (loadError.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:refresh",
                        class: "video-attachment__preview-icon"
                      })
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:play-circle-outline",
                        class: "video-attachment__preview-icon"
                      })
                    ])),
              (_unref(isLoading) && _unref(loadingProgress) > 0)
                ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(Math.round(_unref(loadingProgress))) + "%", 1))
                : (_unref(isLoading) && _unref(loadedBytes) > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(formatFileSize(_unref(loadedBytes))) + " downloaded", 1))
                  : (_unref(isLoading))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, "Loading..."))
                    : (loadError.value)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_9, "Failed — tap to retry"))
                      : (_openBlock(), _createElementBlock("span", _hoisted_10, "Tap to play"))
            ]))
          : (resolvedVideoUrl.value)
            ? (_openBlock(), _createElementBlock("video", {
                key: 1,
                src: resolvedVideoUrl.value,
                controls: "",
                autoplay: "",
                muted: "",
                playsinline: "",
                loop: !!__props.attachment.gif,
                class: "video-attachment__player"
              }, " Your browser does not support the video tag. ", 8, _hoisted_11))
            : _createCommentVNode("", true),
        (videoLoaded.value && resolvedVideoUrl.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 2,
              class: "video-attachment__download",
              onClick: _withModifiers(handleDownload, ["stop"]),
              disabled: _unref(isDownloading),
              title: "Download video"
            }, [
              (_unref(isDownloading) && _unref(downloadProgress) > 0)
                ? (_openBlock(), _createBlock(ProgressCircle, {
                    key: 0,
                    progress: _unref(downloadProgress),
                    size: "small",
                    "bg-stroke": "rgba(255, 255, 255, 0.3)",
                    "fill-stroke": "white",
                    "show-text": false
                  }, null, 8, ["progress"]))
                : (_unref(isDownloading))
                  ? (_openBlock(), _createBlock(_unref(Icon), {
                      key: 1,
                      icon: "mdi:loading",
                      class: "video-attachment__spinner"
                    }))
                  : (_openBlock(), _createBlock(_unref(Icon), {
                      key: 2,
                      icon: "mdi:download"
                    }))
            ], 8, _hoisted_12))
          : _createCommentVNode("", true),
        (__props.attachment.gif)
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [...(_cache[0] || (_cache[0] = [
              _createElementVNode("span", null, "GIF", -1)
            ]))]))
          : _createCommentVNode("", true),
        (__props.attachment.duration)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _createVNode(_unref(Icon), { icon: "mdi:play-circle" }),
              _createElementVNode("span", null, _toDisplayString(formatDuration(__props.attachment.duration)), 1)
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 512))
}
}

})